import type { Workspace } from '../../types'
import { getNormalizedMcpServers, isMcpToolEnabled, type AgentConfig } from '../../agentTypes'
import { RemoteMcpClient, type McpRemoteToolCallResult } from '../../mcp/remoteClient'
import { SECUREBLOB_TOOLS, executeSecureBlobTool, isSecureBlobToolEnabled, type ToolDefinition, type ToolResult } from './tools'

interface ToolExecutor {
    execute: (args: Record<string, unknown>) => Promise<ToolResult>
}

const MAX_TOOL_NAME_LENGTH = 64

export interface AgentToolRegistry {
    definitions: ToolDefinition[]
    execute: (name: string, args: Record<string, unknown>) => Promise<ToolResult>
}

export async function createAgentToolRegistry(
    config: AgentConfig,
    workspace: Workspace,
    fetchImpl: typeof fetch = fetch,
): Promise<AgentToolRegistry> {
    const definitions = getBuiltInToolDefinitions(config)
    const executors = new Map<string, ToolExecutor>()

    for (const tool of definitions) {
        const toolName = tool.function.name
        executors.set(toolName, {
            execute: (args) => executeSecureBlobTool(toolName, args, workspace, config),
        })
    }

    const usedNames = new Set(definitions.map(tool => tool.function.name))
    const mcpServers = getNormalizedMcpServers(config)
    const rawMcpServers = {
        ...(config.mcpServers ?? {}),
        ...(config.mcp ?? {}),
    }
    for (const [serverName, server] of Object.entries(mcpServers)) {
        try {
            const client = new RemoteMcpClient(serverName, server, fetchImpl)
            const remoteTools = await client.listTools()
            const rawEntry = rawMcpServers[serverName]

            for (const tool of remoteTools) {
                if (!isMcpToolEnabled(rawEntry, tool.name)) continue

                const wireName = makeExternalToolWireName(serverName, tool.name, usedNames)
                definitions.push({
                    type: 'function',
                    function: {
                        name: wireName,
                        description: formatExternalToolDescription(serverName, tool),
                        parameters: normalizeToolSchema(tool.inputSchema),
                    },
                })
                executors.set(wireName, {
                    execute: async (args) => {
                        try {
                            const result = await client.callTool(tool.name, args)
                            return {
                                result: formatRemoteToolResult(result),
                                filesModified: [],
                                isError: result.isError === true,
                            }
                        } catch (error) {
                            return {
                                result: formatRemoteToolExecutionError(serverName, tool.name, error),
                                filesModified: [],
                                isError: true,
                            }
                        }
                    },
                })
            }
        } catch (error) {
            console.warn(`[AgentTools] Failed to load MCP tools from "${serverName}"`, error)
        }
    }

    return {
        definitions,
        execute: async (name, args) => {
            const tool = executors.get(name)
            if (!tool) return { result: `Error: Unknown tool ${name}`, filesModified: [] }
            return tool.execute(args)
        },
    }
}

function normalizeToolSchema(schema: Record<string, unknown> | undefined): Record<string, unknown> {
    if (!schema || typeof schema !== 'object') {
        return { type: 'object', properties: {} }
    }
    return schema
}

function formatExternalToolDescription(serverName: string, tool: { title?: string; description?: string; name: string }): string {
    const title = tool.title?.trim()
    const description = tool.description?.trim()
    const prefix = `External MCP tool from "${serverName}" (original tool name: "${tool.name}").`
    if (title && description) return `${prefix} ${title}. ${description}`
    if (description) return `${prefix} ${description}`
    if (title) return `${prefix} ${title}.`
    return prefix
}

function makeExternalToolWireName(serverName: string, toolName: string, usedNames: Set<string>): string {
    const serverPart = sanitizeToolName(serverName)
    const toolPart = sanitizeToolName(toolName)
    const baseName = `mcp_${serverPart}__${toolPart}`
    let suffix = ''
    let candidate = truncateToolName(baseName, suffix)
    let suffixIndex = 2
    while (usedNames.has(candidate)) {
        suffix = `_${suffixIndex++}`
        candidate = truncateToolName(baseName, suffix)
    }
    usedNames.add(candidate)
    return candidate
}

function sanitizeToolName(value: string): string {
    const sanitized = value.replace(/[^A-Za-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')
    return sanitized || 'tool'
}

function truncateToolName(value: string, suffix: string): string {
    const maxBaseLength = Math.max(1, MAX_TOOL_NAME_LENGTH - suffix.length)
    const base = value.length <= maxBaseLength ? value : value.slice(0, maxBaseLength)
    return `${base}${suffix}`
}

function getBuiltInToolDefinitions(config: AgentConfig): ToolDefinition[] {
    return SECUREBLOB_TOOLS.filter((tool) => isSecureBlobToolEnabled(config, tool.function.name))
}

function formatRemoteToolResult(result: McpRemoteToolCallResult): string {
    const parts = Array.isArray(result.content) ? result.content : []
    const formatted = parts
        .map(formatRemoteContentBlock)
        .filter(Boolean)
        .join('\n\n')

    const structured = result.structuredContent
        ? JSON.stringify(result.structuredContent, null, 2)
        : ''

    const body = [formatted, structured].filter(Boolean).join('\n\n')
    if (body) {
        return result.isError ? `Error: ${body}` : body
    }
    return result.isError ? 'Error: MCP tool returned an error without details' : 'MCP tool executed successfully'
}

function formatRemoteContentBlock(block: unknown): string {
    if (!block || typeof block !== 'object') return String(block ?? '')
    const candidate = block as Record<string, unknown>
    if (candidate.type === 'text' && typeof candidate.text === 'string') return candidate.text
    if (candidate.type === 'resource' && candidate.resource && typeof candidate.resource === 'object') {
        return JSON.stringify(candidate.resource, null, 2)
    }
    return JSON.stringify(candidate, null, 2)
}

function formatRemoteToolExecutionError(serverName: string, toolName: string, error: unknown): string {
    const payload = {
        error: 'MCP tool execution failed',
        server: serverName,
        tool: toolName,
        message: error instanceof Error ? error.message : String(error),
    }
    return JSON.stringify(payload, null, 2)
}
